import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import {
  AppButton,
  AppFormField,
  AppInput,
  AppSpinner,
} from '../ui';
import '../../styles/iti-referentiel-admin.scss';

const emptyCategoryForm = { libelle: '', icone: '', sort_order: 0 };

function ToggleActiveButton({ active, disabled, onChange, labelId }) {
  return (
    <AppButton
      size="sm"
      variant={active ? 'outline-warning' : 'outline-success'}
      disabled={disabled}
      onClick={() => onChange(!active)}
      id={labelId}
      title={active ? 'Suspendre (masquer dans les filtres)' : 'Réactiver (afficher dans les filtres)'}
      icon={active ? 'mdi:pause' : 'mdi:play'}
    />
  );
}

/**
 * Panneau admin réutilisable pour secteurs, types de site, fonctionnalités.
 */
export default function ItiReferentielAdminPanel({
  className = '',
  addOptionLabel = 'Ajouter une option',
  emptyOptionMessage = 'Aucune option pour le moment. Utilisez le champ ci-dessus pour en ajouter une.',
  optionCountSingular = 'option',
  optionCountPlural = 'options',
  optionPlaceholder = 'Nom de l\'option…',
  categoryPlaceholder = 'Nom de la catégorie…',
  categories = [],
  loading = false,
  saving = false,
  expanded = {},
  onToggleCategory,
  showCategoryForm = false,
  onToggleCategoryForm,
  categoryForm = emptyCategoryForm,
  onCategoryFormChange,
  onCreateCategory,
  optionForms = {},
  onOptionFormChange,
  onCreateOption,
  onUpdateCategory,
  onDeleteCategory,
  onUpdateOption,
  onDeleteOption,
  extraColumns = [],
  footerSlot = null,
}) {
  const [settingsOpen, setSettingsOpen] = useState({});

  const totalOptions = categories.reduce((sum, cat) => sum + (cat.options?.length ?? 0), 0);

  if (loading) {
    return <AppSpinner />;
  }

  const toggleSettings = (categoryId) => {
    setSettingsOpen((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  return (
    <div className={`iti-referentiel-admin ${className}`.trim()}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <p className="iti-ref-stats mb-0">
          {categories.length} catégorie{categories.length > 1 ? 's' : ''} · {totalOptions} {totalOptions > 1 ? optionCountPlural : optionCountSingular}
        </p>
        <AppButton
          disabled={saving}
          onClick={onToggleCategoryForm}
          icon="mdi:plus"
        >
          Nouvelle catégorie
        </AppButton>
      </div>

      {showCategoryForm && (
        <form className="iti-ref-add-category mb-3" onSubmit={onCreateCategory}>
          <p className="fw-semibold mb-3">Créer une catégorie</p>
          <div className="row g-3 align-items-end">
            <AppFormField className="col-md-6 mb-0" label="Nom de la catégorie">
              <AppInput
                type="text"
                value={categoryForm.libelle}
                onChange={(e) => onCategoryFormChange({ ...categoryForm, libelle: e.target.value })}
                placeholder={categoryPlaceholder}
                required
                autoFocus
              />
            </AppFormField>
            <AppFormField className="col-md-2 mb-0" label={<>Icône <span className="text-muted fw-normal">(facultatif)</span></>}>
              <AppInput
                type="text"
                value={categoryForm.icone}
                onChange={(e) => onCategoryFormChange({ ...categoryForm, icone: e.target.value })}
                placeholder="🏠"
              />
            </AppFormField>
            <div className="col-md-4 d-flex flex-wrap gap-2">
              <AppButton type="submit" variant="success" loading={saving} icon="mdi:check">
                Enregistrer
              </AppButton>
              <AppButton variant="outline-secondary" disabled={saving} onClick={onToggleCategoryForm}>
                Annuler
              </AppButton>
            </div>
          </div>
        </form>
      )}

      <div className="d-flex flex-column gap-3">
        {categories.map((category) => {
          const optionForm = optionForms[category.id] ?? { libelle: '', sort_order: 0 };
          const isOpen = expanded[category.id] === true;
          const showSettings = settingsOpen[category.id] === true;
          const options = category.options ?? [];

          return (
            <div
              key={category.id}
              className={`iti-ref-category-card ${!category.active ? 'is-hidden' : ''}`}
            >
              <div className="iti-ref-category-header">
                <button
                  type="button"
                  className="iti-ref-category-toggle"
                  onClick={() => onToggleCategory(category.id)}
                  aria-expanded={isOpen}
                >
                  <Icon icon={isOpen ? 'mdi:chevron-down' : 'mdi:chevron-right'} width={22} />
                  {category.icone && category.icone !== '?' && (
                    <span className="searchable-grouped-select__icon" aria-hidden="true">{category.icone}</span>
                  )}
                  <span>{category.libelle}</span>
                  <span className="badge bg-light text-secondary">{options.length}</span>
                </button>

                <div className="ms-auto d-flex flex-wrap align-items-center gap-1">
                  <ToggleActiveButton
                    active={category.active}
                    disabled={saving}
                    onChange={(active) => onUpdateCategory(category, { active })}
                    labelId={`cat-vis-${category.id}`}
                  />
                  <AppButton
                    size="sm"
                    variant="outline-secondary"
                    disabled={saving}
                    onClick={() => toggleSettings(category.id)}
                    title="Paramètres de la catégorie"
                    icon="mdi:cog-outline"
                  />
                  <AppButton
                    size="sm"
                    variant="outline-danger"
                    disabled={saving}
                    onClick={() => onDeleteCategory(category)}
                    title="Supprimer la catégorie"
                    icon="mdi:delete-outline"
                  />
                </div>
              </div>

              {showSettings && (
                <div className="iti-ref-settings">
                  <p className="small fw-semibold text-secondary mb-2">Paramètres de la catégorie</p>
                  <div className="row g-2 align-items-end">
                    <AppFormField className="col-md-5 mb-0" label="Nom">
                      <AppInput
                        size="sm"
                        type="text"
                        defaultValue={category.libelle}
                        disabled={saving}
                        key={`cat-libelle-${category.id}-${category.libelle}`}
                        onBlur={(e) => {
                          const next = e.target.value.trim();
                          if (next && next !== category.libelle) {
                            onUpdateCategory(category, { libelle: next });
                          }
                        }}
                      />
                    </AppFormField>
                    <AppFormField className="col-md-2 mb-0" label="Icône">
                      <AppInput
                        size="sm"
                        type="text"
                        defaultValue={category.icone ?? ''}
                        disabled={saving}
                        key={`cat-icone-${category.id}-${category.icone ?? ''}`}
                        placeholder="🏠"
                        onBlur={(e) => {
                          const next = e.target.value.trim();
                          if (next !== (category.icone ?? '')) {
                            onUpdateCategory(category, { icone: next || null });
                          }
                        }}
                      />
                    </AppFormField>
                  </div>
                </div>
              )}

              {isOpen && (
                <div className="p-3">
                  <div className="iti-ref-add-option mb-3">
                    <p className="fw-semibold mb-2">
                      <Icon icon="mdi:plus-circle-outline" className="me-1" width={18} />
                      {addOptionLabel}
                    </p>
                    <form
                      className="d-flex flex-wrap gap-2 align-items-stretch"
                      onSubmit={(e) => onCreateOption(e, category.id)}
                    >
                      <AppInput
                        type="text"
                        className="flex-grow-1"
                        style={{ minWidth: '12rem' }}
                        value={optionForm.libelle}
                        onChange={(e) => onOptionFormChange(category.id, { ...optionForm, libelle: e.target.value })}
                        placeholder={optionPlaceholder}
                        disabled={saving}
                      />
                      <AppButton type="submit" className="px-4" disabled={saving || !optionForm.libelle?.trim()}>
                        Ajouter
                      </AppButton>
                    </form>
                  </div>

                  {options.length === 0 ? (
                    <div className="iti-ref-empty border rounded">
                      {emptyOptionMessage}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover iti-ref-options-table mb-0">
                        <thead>
                          <tr>
                            <th>Nom</th>
                            {extraColumns.map((col) => (
                              <th key={col.header}>{col.header}</th>
                            ))}
                            <th style={{ width: 52 }} aria-label="Actions" />
                            <th style={{ width: 52 }} aria-label="Supprimer" />
                          </tr>
                        </thead>
                        <tbody>
                          {options.map((option) => (
                            <tr key={option.id} className={!option.active ? 'text-secondary' : undefined}>
                              <td>
                                <AppInput
                                  size="sm"
                                  type="text"
                                  defaultValue={option.libelle}
                                  disabled={saving}
                                  onBlur={(e) => {
                                    const next = e.target.value.trim();
                                    if (next && next !== option.libelle) {
                                      onUpdateOption(option, { libelle: next });
                                    }
                                  }}
                                />
                              </td>
                              {extraColumns.map((col) => (
                                <td key={`${option.id}-${col.header}`}>{col.render(option)}</td>
                              ))}
                              <td>
                                <ToggleActiveButton
                                  active={option.active}
                                  disabled={saving}
                                  onChange={(active) => onUpdateOption(option, { active })}
                                  labelId={`opt-vis-${option.id}`}
                                />
                              </td>
                              <td className="text-end">
                                <AppButton
                                  size="sm"
                                  variant="outline-danger"
                                  disabled={saving}
                                  onClick={() => onDeleteOption(option)}
                                  title={`Supprimer « ${option.libelle} »`}
                                  icon="mdi:delete-outline"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {footerSlot}
    </div>
  );
}

export { emptyCategoryForm };
